import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Space, Switch, Tooltip, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  IndoorPositioningEquipment,
  IndoorPositioningPersonnel,
} from "../../../api";
import { getSymbolById } from "../FloorPlanModal/utils/symbolTypes";
import useEquipment from "./hooks/useEquipment";
import EquipmentFormModal from "./components/EquipmentFormModal";
import EquipmentTagsModal from "./components/EquipmentTagsModal";
import ProtectionZonePersonnelModal from "../FloorPlanModal/components/ProtectionZonePersonnelModal";

const EquipmentModal = ({ open, onClose, machineID }) => {
  const { equipment, loading, loadEquipment } = useEquipment(machineID);
  const [personnel, setPersonnel] = useState([]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [tagsEquipmentId, setTagsEquipmentId] = useState(null);
  // Look the machine up fresh from `equipment` each render so the tags modal
  // reflects the latest data as soon as loadEquipment() refreshes the list.
  const tagsEquipment = equipment.find((item) => item.id === tagsEquipmentId);

  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [personnelEquipmentId, setPersonnelEquipmentId] = useState(null);
  const [personnelConfirming, setPersonnelConfirming] = useState(false);
  // Same fresh-lookup reason as tagsEquipment above: read the record out of
  // `equipment` each render so the picker reflects loadEquipment()'s refresh.
  const personnelEquipment = equipment.find(
    (item) => item.id === personnelEquipmentId,
  );

  const loadPersonnel = async () => {
    try {
      const response = await IndoorPositioningPersonnel.GetPersonnel(machineID);
      if (response.code === 200) {
        setPersonnel(response.data || []);
      }
    } catch (error) {
      console.error("載入人員資料失敗:", error);
    }
  };

  useEffect(() => {
    if (open) {
      loadEquipment();
      loadPersonnel();
    }
  }, [open, machineID]);

  const handleAdd = () => {
    setEditingRecord(null);
    setFormModalOpen(true);
  };

  const handleManagePersonnel = (record) => {
    setPersonnelEquipmentId(record.id);
    setPersonnelModalOpen(true);
  };

  const handleConfirmPersonnel = async (equipmentId, ids) => {
    setPersonnelConfirming(true);
    try {
      const response = await IndoorPositioningEquipment.UpdateEquipment(
        equipmentId,
        { allowed_personnel_ids: ids },
      );
      if (response.code === 200) {
        message.success("更新允許接近人員成功");
        loadEquipment();
      } else {
        message.error(response.message || "更新允許接近人員失敗");
        throw new Error(response.message || "更新允許接近人員失敗");
      }
    } finally {
      setPersonnelConfirming(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: `確定要刪除機具「${record.name}」嗎？此機具底下的所有定位器也會一併刪除。`,
      okText: "確認",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await IndoorPositioningEquipment.DeleteEquipment(
            record.id,
          );
          if (response.code === 200) {
            message.success("刪除機具成功");
            loadEquipment();
          } else {
            message.error(response.message || "刪除機具失敗");
          }
        } catch (error) {
          console.error("刪除機具失敗:", error);
          message.error("刪除機具失敗");
        }
      },
    });
  };

  const handleToggleAlert = async (record, checked) => {
    const response = await IndoorPositioningEquipment.UpdateEquipment(
      record.id,
      { alert_enabled: checked },
    );
    if (response.code === 200) {
      loadEquipment();
    } else {
      message.error(response.message || "更新警報狀態失敗");
    }
  };

  const updateActive = async (record, isActive) => {
    const response = await IndoorPositioningEquipment.UpdateEquipment(
      record.id,
      { is_active: isActive },
    );
    if (response.code === 200) {
      loadEquipment();
    } else {
      message.error(response.message || "更新使用狀態失敗");
    }
  };

  // Deliberately asymmetric with 警報開關 above (which has no confirm):
  // turning a machine OFF makes it vanish from the live canvas AND stops it
  // ever alarming, so it gets one beat of friction. Turning it back ON is
  // reversible and harmless, so it doesn't.
  const handleToggleActive = (record, checked) => {
    if (checked) {
      updateActive(record, true);
      return;
    }
    Modal.confirm({
      title: "確認停用機具",
      content: `停用「${record.name}」後，此機具將從即時監控畫布上完全消失，也不會再對接近的人員發出警報。機具設定與定位器資料仍會保留，可隨時重新啟用。`,
      okText: "確認停用",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: () => updateActive(record, false),
    });
  };

  const handleManageTags = (record) => {
    setTagsEquipmentId(record.id);
    setTagsModalOpen(true);
  };

  const columns = [
    {
      title: "圖示",
      dataIndex: "symbol_type",
      key: "symbol_type",
      align: "center",
      render: (symbolType) => {
        const symbol = getSymbolById(symbolType);
        if (!symbol) return symbolType;
        const Icon = symbol.icon;
        return <Icon style={{ fontSize: 20, color: symbol.color }} />;
      },
    },
    {
      title: "名稱",
      dataIndex: "name",
      key: "name",
      align: "center",
      // Without a floor the name is the column the browser chooses to
      // squeeze, so "35噸吊車" broke across three lines.
      width: 120,
    },
    {
      title: "警戒半徑",
      dataIndex: "alert_radius_meters",
      key: "alert_radius_meters",
      align: "center",
      render: (val) => (val != null ? `${val} m` : "-"),
    },
    {
      title: "機具長度",
      dataIndex: "span_meters",
      key: "span_meters",
      align: "center",
      render: (val) => (val != null ? `${val} m` : "未設定"),
    },
    {
      title: "允許接近人員",
      dataIndex: "allowed_personnel_ids",
      key: "allowed_personnel_ids",
      align: "center",
      // NULL is NOT "0 people allowed" -- it means no list has been set, and
      // the alarm then treats EVERYONE as unauthorised (see the backend's
      // proximity check). Showing "0 人" for that would read as the exact
      // opposite of what it does, so the unset case says so in words.
      render: (ids, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleManagePersonnel(record)}
        >
          {Array.isArray(ids) ? `${ids.length} 人` : "未設定"}
        </Button>
      ),
    },
    {
      title: "定位器",
      dataIndex: "tags",
      key: "tags",
      align: "center",
      render: (tags, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleManageTags(record)}
        >
          {(tags || []).length} 個
        </Button>
      ),
    },
    {
      title: "使用中",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (checked, record) => (
        <Switch
          checked={checked !== false}
          checkedChildren="開"
          unCheckedChildren="關"
          onChange={(value) => handleToggleActive(record, value)}
        />
      ),
    },
    {
      title: "警報開關",
      dataIndex: "alert_enabled",
      key: "alert_enabled",
      align: "center",
      render: (checked, record) => (
        <Tooltip
          title={record.is_active === false ? "機具已停用，不會發出警報" : ""}
        >
          <Switch
            checked={!!checked}
            checkedChildren="開"
            unCheckedChildren="關"
            disabled={record.is_active === false}
            onChange={(value) => handleToggleAlert(record, value)}
          />
        </Tooltip>
      ),
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="機具資料"
        open={open}
        onCancel={onClose}
        footer={null}
        width={1200}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="font-weight-bold mr-2">機具總數：</span>
              <span>{equipment.length} 台</span>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增機具
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={equipment}
            // The row is now wider than a narrow viewport allows. antd caps
            // the modal at calc(100vw - 32px), so without this the table
            // spills out past the modal's edge instead of scrolling inside it.
            scroll={{ x: "max-content" }}
            rowKey="id"
            loading={loading}
            // De-emphasise (not hide) a 停用 machine's row so it stays
            // readable but visually reads as "not currently in play".
            onRow={(record) => ({
              style: record.is_active === false ? { opacity: 0.55 } : {},
            })}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            locale={{ emptyText: "暫無資料" }}
          />
        </div>
      </Modal>

      <EquipmentFormModal
        open={formModalOpen}
        editingRecord={editingRecord}
        machineID={machineID}
        onClose={() => setFormModalOpen(false)}
        onSaved={() => {
          setFormModalOpen(false);
          loadEquipment();
        }}
      />

      <EquipmentTagsModal
        open={tagsModalOpen}
        equipment={tagsEquipment}
        onClose={() => setTagsModalOpen(false)}
        onChanged={loadEquipment}
      />

      <ProtectionZonePersonnelModal
        open={personnelModalOpen}
        station={
          personnelEquipment
            ? {
                id: personnelEquipment.id,
                allowed_personnel_ids:
                  personnelEquipment.allowed_personnel_ids ?? null,
              }
            : null
        }
        personnel={personnel}
        onClose={() => setPersonnelModalOpen(false)}
        onConfirm={handleConfirmPersonnel}
        confirming={personnelConfirming}
      />
    </>
  );
};

export default EquipmentModal;
